import React, { useState, useEffect, useCallback } from "react";
import { Menu, LogOut, Bell, Search, X, Sun, Moon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { loginUrl } from "../lib/portal";
import { api, formatDateTimeBR } from "../lib/api";
import { useTheme } from "../lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { PendenciaDot } from "./pendenciaDocsContext";
import { Badge } from "./ui/badge";

const ROLE_LABEL = { admin: "Admin Parceiro", operator: "Operador", viewer: "Visualizador" };

export default function Header({ onOpenSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOffline, setNotifOffline] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
      setNotifOffline(false);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load notifications:", e);
      setNotifOffline(true);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((ns) => ns.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error("Failed to mark notifications as read:", e);
    }
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-[var(--zemo-border)]">
      <div className="h-16 px-4 lg:px-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            className="lg:hidden p-2 -ml-2 rounded hover:bg-zinc-100"
            onClick={onOpenSidebar}
            data-testid="open-sidebar-button"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[var(--zemo-border)] bg-zinc-50/80 text-xs text-zinc-500 w-72">
            <Search className="h-3.5 w-3.5" />
            <span>Buscar operações, cedentes...</span>
            <span className="ml-auto font-mono text-[10px] text-zinc-400">&#8984;K</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <button
            className="p-2 rounded hover:bg-zinc-100"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="theme-toggle-btn"
            aria-label="Alternar tema"
            title={theme === "dark" ? "Tema claro" : "Tema escuro"}
          >
            {theme === "dark" ? <Sun className="h-4 w-4 text-zinc-600" /> : <Moon className="h-4 w-4 text-zinc-600" />}
          </button>
          {/* Notifications */}
          <div className="relative">
            <button
              className="p-2 rounded hover:bg-zinc-100 relative"
              onClick={() => setNotifOpen(!notifOpen)}
              data-testid="notifications-button"
              aria-label="Notificações"
            >
              <Bell className="h-4.5 w-4.5 text-zinc-600" />
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#0055FF]" data-testid="notif-unread-dot"></span>
              )}
              {notifOffline && (
                <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-amber-400" data-testid="notif-offline-dot" title="Sincronização indisponível"></span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg border border-[var(--zemo-border)] shadow-lg z-50" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--zemo-border)]">
                  <span className="text-sm font-semibold">Notificações</span>
                  <button onClick={() => setNotifOpen(false)} className="text-zinc-400 hover:text-zinc-600"><X className="h-4 w-4" /></button>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-[var(--zemo-border)]">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-zinc-500">
                      {notifOffline ? (
                        <div className="flex flex-col items-center gap-1.5">
                          <span className="text-amber-600 text-xs font-medium">Sincronização indisponível</span>
                          <span>Tentando reconectar...</span>
                        </div>
                      ) : "Nenhuma notificação"}
                    </div>
                  ) : notifications.map((n) => (
                    <div key={n.id} className={`px-4 py-3 text-sm ${n.is_read ? "opacity-60" : ""}`} data-testid={`notif-item-${n.id}`}>
                      <div className="text-zinc-900 font-medium">{n.title}</div>
                      <div className="text-zinc-600 text-xs mt-0.5">{n.message}</div>
                      <div className="text-[11px] text-zinc-400 mt-1">{formatDateTimeBR(n.created_at)}</div>
                    </div>
                  ))}
                </div>
                {unreadCount > 0 && (
                  <div className="px-4 py-2 border-t border-[var(--zemo-border)]">
                    <button className="text-[12px] text-[#0055FF] font-semibold hover:underline" onClick={markAllRead} data-testid="notif-mark-all-read">
                      Marcar todas como lidas
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-md hover:bg-zinc-50 border border-transparent hover:border-[var(--zemo-border)] transition-colors"
                data-testid="user-menu-trigger"
              >
                <span className="relative inline-flex">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-zinc-900 text-white text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <PendenciaDot testid="user-menu-pendencia-dot" className="absolute -top-0.5 -right-0.5 ring-2 ring-white" />
                </span>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-[13px] font-semibold text-zinc-900">{user?.name}</div>
                  <div className="text-[11px] text-zinc-500">{ROLE_LABEL[user?.role] || user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="flex flex-col gap-1">
                <span className="font-semibold">{user?.name}</span>
                <span className="text-xs text-zinc-500 font-normal">{user?.email}</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                    {ROLE_LABEL[user?.role] || user?.role}
                  </Badge>
                  {user?.two_factor_enabled && (
                    <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                      2FA ativo
                    </Badge>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-profile">
                Perfil &amp; 2FA
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  window.location.href = loginUrl();
                }}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                data-testid="menu-logout"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
